import React, { useMemo, useState } from 'react';
import SortableTable from '../../SortableTable';
import { buildUrlIssue } from '../../../utils/url';
import {
  formatTimeDiff,
  formatDateFr,
  formatTimeFr,
  getStatusBadgeClasses,
  parseProblems,
  getPictureSrc,
  getPictureCaption,
} from '../ipHelpers';

const refValue = (call) => (call.type === 'GPS' && call.issueKey ? call.issueKey : `#${call.id}`);
const typeValue = (call) => (call.type === 'GPS' ? call.requestType || '' : call.projectName || '');

// A GPS issue with a real Jira ticket (non-CSV) links to its Jira service desk issue
const jiraHref = (call) =>
  call.type === 'GPS' && call.source !== 'CSV' ? buildUrlIssue(call.projectName, call.issueKey) : null;

function RefCell({ call, className = '' }) {
  const label = refValue(call);
  const href = jiraHref(call);
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`font-mono text-xs text-blue-600 dark:text-blue-400 hover:underline ${className}`}
        title="Ouvrir dans Jira"
      >
        {label} ↗
      </a>
    );
  }
  return <span className={`font-mono text-xs text-gray-500 dark:text-gray-400 ${className}`}>{label}</span>;
}

const ViewToggle = ({ view, setView }) => (
  <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-sm">
    {[
      { id: 'table', label: 'Tableau' },
      { id: 'timeline', label: 'Timeline' },
    ].map((o) => (
      <button
        key={o.id}
        type="button"
        onClick={() => setView(o.id)}
        className={`px-3 py-1.5 ${
          view === o.id
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

function HistoryTable({ calls, storeName }) {
  const columns = useMemo(
    () => [
      {
        key: 'date',
        label: 'Date',
        rawValue: (c) => c.date || '',
        // Compare by timestamp (TH and GPS date strings have different formats → lexicographic sort is wrong)
        sortFunction: (a, b, dir) => {
          const ta = a.date ? new Date(a.date).getTime() : 0;
          const tb = b.date ? new Date(b.date).getTime() : 0;
          return dir === 'ascending' ? ta - tb : tb - ta;
        },
        render: (c) => (
          <span className="whitespace-nowrap">
            <span className="text-gray-800 dark:text-gray-200">{formatDateFr(c.date, { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            <span className="text-gray-400 dark:text-gray-500 ml-2">{formatTimeFr(c.date)}</span>
          </span>
        ),
      },
      {
        key: 'ref',
        label: 'Réf',
        rawValue: refValue,
        render: (c) => <RefCell call={c} />,
      },
      {
        key: 'type',
        label: 'Type',
        rawValue: typeValue,
        render: (c) => <span className="text-gray-700 dark:text-gray-300">{typeValue(c) || '—'}</span>,
      },
      {
        key: 'brand',
        label: 'Marque',
        rawValue: (c) => c.brand || '',
        render: (c) => c.brand || '—',
      },
      {
        key: 'furnitures',
        label: 'Mobilier',
        rawValue: (c) => c.furnitures || '',
        render: (c) => <span className="text-xs text-gray-500 dark:text-gray-400">{c.furnitures || '—'}</span>,
      },
      {
        key: 'status',
        label: 'Statut',
        rawValue: (c) => c.status || 'Réalisée',
        render: (c) =>
          c.status ? (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadgeClasses(c.status)}`}>{c.status}</span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500 text-xs">Réalisée</span>
          ),
      },
    ],
    []
  );

  return (
    <SortableTable
      data={calls}
      columns={columns}
      initialSortBy={{ key: 'date', direction: 'descending' }}
      globalSearch
      exportable
      exportRenamable
      exportFilename={`historique-${(storeName || 'magasin').replace(/[^\w-]+/g, '_')}.csv`}
      countLabel={`${calls.length} visite${calls.length > 1 ? 's' : ''}`}
      emptyMessage="Aucun passage disponible."
    />
  );
}

function HistoryTimeline({ calls, picturesByCallId, onOpenPhotos }) {
  const timelineData = useMemo(() => {
    const sorted = [...calls].filter((c) => c.date).sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted.map((call, index) => {
      let durationSincePrev = null;
      if (index < sorted.length - 1) {
        const diffMs = new Date(call.date) - new Date(sorted[index + 1].date);
        durationSincePrev = diffMs / (1000 * 60);
      }
      return { ...call, durationSincePrev };
    });
  }, [calls]);

  if (timelineData.length === 0) {
    return <p className="text-gray-500 dark:text-gray-400 italic text-center py-8">Aucun passage daté.</p>;
  }

  return (
    <div className="relative">
      <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 h-full w-1.5 bg-gray-200 dark:bg-gray-700" />
      <div className="space-y-0">
        {timelineData.map((call, index) => (
          <div key={call.id} className="relative">
            {call.durationSincePrev !== null && (
              <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 -bottom-6 z-10 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs px-2 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                ↓ {formatTimeDiff(call.durationSincePrev)}
              </div>
            )}
            <div className={`flex items-center justify-between w-full mb-12 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
              <div className="w-full pl-16 md:pl-0 md:w-5/12">
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-2">
                    <RefCell call={call} />
                    {call.type === 'GPS' && (
                      <span className="text-xs font-medium bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">GPS</span>
                    )}
                  </div>
                  <div className="font-bold text-gray-800 dark:text-white mb-1">{formatDateFr(call.date)}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">{formatTimeFr(call.date)}</div>

                  {call.type !== 'GPS' && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-800 px-2 py-1 rounded inline-block truncate max-w-full" title={call.projectName}>
                      {call.projectName}
                    </div>
                  )}

                  {call.type === 'GPS' && (
                    <div className="mt-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {call.requestType && (
                          <span className="text-xs text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-800 px-2 py-0.5 rounded">{call.requestType}</span>
                        )}
                        {call.status && (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadgeClasses(call.status)}`}>{call.status}</span>
                        )}
                      </div>
                      {(call.brand || call.furnitures || call.dueDate) && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
                          {call.brand && (<div><span className="text-gray-400 dark:text-gray-500">Marque:</span> <span className="font-medium">{call.brand}</span></div>)}
                          {call.furnitures && (<div><span className="text-gray-400 dark:text-gray-500">Mobilier:</span> <span className="font-medium">{call.furnitures}</span></div>)}
                          {call.dueDate && (<div><span className="text-gray-400 dark:text-gray-500">Intervention:</span> <span className="font-medium">{formatDateFr(call.dueDate, { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>)}
                        </div>
                      )}
                      {parseProblems(call.problems).length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {parseProblems(call.problems).map((problem, pIdx) => (
                            <span key={pIdx} className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-100 dark:border-red-900/50">
                              {problem}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {(picturesByCallId[call.id] || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {(picturesByCallId[call.id] || []).slice(0, 5).map((pic, idx) => {
                        const src = getPictureSrc(pic);
                        if (!src) return null;
                        const caption = getPictureCaption(pic);
                        return (
                          <button
                            key={`${call.id}-${idx}`}
                            type="button"
                            onClick={() => onOpenPhotos(call.id, picturesByCallId[call.id], `Visite #${call.id}`)}
                            className="focus:outline-none w-16"
                          >
                            <div className="relative w-full h-16 rounded overflow-hidden border border-gray-200 dark:border-gray-700">
                              <img src={src} alt={caption || 'photo'} loading="lazy" className="w-full h-full object-cover" />
                            </div>
                          </button>
                        );
                      })}
                      {(picturesByCallId[call.id] || []).length > 5 && (
                        <button
                          type="button"
                          onClick={() => onOpenPhotos(call.id, picturesByCallId[call.id], `Visite #${call.id}`)}
                          className="w-16 h-16 flex items-center justify-center rounded border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900"
                        >
                          +{(picturesByCallId[call.id] || []).length - 5}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 w-6 h-6 md:w-8 md:h-8 bg-blue-500 rounded-full border-4 border-white dark:border-gray-800 shadow-sm z-10" />
              <div className="hidden md:block w-5/12" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function IPHistoriqueTab({ visitCalls, picturesByCallId, onOpenPhotos, openOnly, setOpenOnly, storeName }) {
  const [view, setView] = useState('table');

  return (
    <div>
      <div className="flex items-center justify-end gap-3 flex-wrap mb-4">
        {openOnly && (
          <button
            type="button"
            onClick={() => setOpenOnly(false)}
            className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 inline-flex items-center gap-1 mr-auto"
          >
            Ouvertes uniquement ✕
          </button>
        )}
        <ViewToggle view={view} setView={setView} />
      </div>

      {view === 'table' ? (
        <HistoryTable calls={visitCalls} storeName={storeName} />
      ) : (
        <HistoryTimeline calls={visitCalls} picturesByCallId={picturesByCallId} onOpenPhotos={onOpenPhotos} />
      )}
    </div>
  );
}
